import { Command } from "commander";
import { loginCommand } from "./commands/login.js";
import { deployCommand } from "./commands/deploy.js";
import { lsCommand } from "./commands/ls.js";
import { logsCommand } from "./commands/logs.js";
import { rmCommand } from "./commands/rm.js";
import { openCommand } from "./commands/open.js";
import { whoamiCommand } from "./commands/whoami.js";
import { initCommand } from "./commands/init.js";
import { devCommand } from "./commands/dev.js";
import { linkCommand } from "./commands/link.js";
import { pullSchemaCommand } from "./commands/pull.js";
import { orgListCommand, orgUseCommand } from "./commands/org.js";
import { workspaceInitCommand } from "./commands/workspace.js";
import { executeCommand } from "./commands/execute.js";
import { docsCommand } from "./commands/docs.js";
import { mcpBridgeCommand } from "./commands/mcp-bridge.js";
import { sdkInspectCommand, sdkLoginCommand, sdkInstallCommand } from "./commands/sdk.js";
import {
  skillsAddCommand,
  skillsListCommand,
  skillsRemoveCommand,
} from "./commands/skills.js";
import {
  aiFilesInstallCommand,
  aiFilesUninstallCommand,
} from "./commands/ai-files.js";

export async function runCli() {
  const program = new Command();

  program
    .name("clawnify")
    .description("Deploy apps to Clawnify")
    .version("0.1.0");

  program
    .command("init [dir]")
    .description("Scaffold a new Clawnify project (app or website)")
    .option("--type <type>", "Project type: app or website (skips prompt)")
    .option("--name <name>", "Project name (skips prompt)")
    .option("--template <template>", "App template: blank or crud (apps only)")
    .option("--storage", "Enable file uploads (R2) — apps only")
    .option("-y, --yes", "Non-interactive: use all defaults (app, blank, no storage)")
    .option("--no-ai-files", "Skip installing AI files (rules + skills + MCP) after scaffolding")
    .action(initCommand);

  program
    .command("dev [dir]")
    .description("Start local dev server")
    .option("--port <port>", "API server port (default: 8787)")
    .action(devCommand);

  program
    .command("login")
    .description("Authenticate with Clawnify")
    .action(loginCommand);

  program
    .command("deploy [dir]")
    .description("Deploy an app or website to Clawnify")
    .option("--from <repo>", "Deploy from a GitHub repo (owner/repo)")
    .option("--path <subdir>", "Subdirectory within the --from repo that holds the app (monorepo)")
    .option("--name <name>", "Override app name")
    .option("--org <slug-or-id>", "Deploy to a specific org (overrides active org)")
    .option("-y, --yes", "Non-interactive: on slug conflict, update existing app")
    .option("--update-existing", "Non-interactive: on slug conflict, overwrite existing app")
    .option("--create-new", "Non-interactive: on slug conflict, create a new app with the suggested slug")
    .option("--dry-run", "List files that would be uploaded; do not deploy")
    .option("--no-sync", "Skip the post-deploy schema.sql sync into the local working directory")
    .action(deployCommand);

  program
    .command("link <app-id-or-slug>")
    .description("Link the current directory to an existing Clawnify app (accepts id or slug)")
    .option("--dir <dir>", "Directory to link (default: cwd)")
    .option("--org <slug-or-id>", "Restrict the app lookup to a specific org")
    .action(linkCommand);

  // `clawnify pull` — sync deployed-app artifacts back into the local
  // working directory. Currently only `schema` is supported; future
  // subcommands (migrations, manifest, etc.) can slot in here.
  const pull = program
    .command("pull")
    .description("Pull artifacts from a deployed app into the current directory");

  pull
    .command("schema")
    .description("Fetch the deployed app's current schema.sql snapshot into the local repo")
    .option("--from <app-id-or-slug>", "App to fetch from (required)")
    .option("--out <path>", "Where to write (default: ./schema.sql)")
    .option("--force", "Overwrite a locally-modified or uncommitted schema.sql")
    .option("--org <slug-or-id>", "Restrict the app lookup to a specific org")
    .action(pullSchemaCommand);

  const org = program.command("org").description("Manage organizations");
  org.command("list").description("List orgs you belong to").action(orgListCommand);
  org
    .command("use <slug-or-id>")
    .description("Set the active org for global commands (ls, whoami, etc.)")
    .action(orgUseCommand);

  // `clawnify workspace` — a monorepo hosting one org's apps. Apps scaffolded
  // inside it land under apps/ and inherit the org on deploy.
  const workspace = program
    .command("workspace")
    .description("Manage a monorepo that hosts an org's apps");
  workspace
    .command("init [dir]")
    .description("Scaffold a workspace monorepo bound to an org")
    .option("--org <slug-or-id>", "Org to bind the workspace to (defaults to active org)")
    .option("--name <name>", "Workspace display name (defaults to the org name)")
    .action(workspaceInitCommand);

  program
    .command("ls")
    .description("List deployed apps")
    .action(lsCommand);

  program
    .command("logs <app-id>")
    .description("Show build logs for an app")
    .action(logsCommand);

  program
    .command("rm <app-id>")
    .description("Delete an app")
    .action(rmCommand);

  program
    .command("open <slug>")
    .description("Open an app in the browser")
    .action(openCommand);

  program
    .command("whoami")
    .description("Show current user info")
    .action(whoamiCommand);

  program
    .command("execute [file]")
    .description("Run TypeScript code on the active org's substrate via mcp.clawnify.com")
    .option("-c, --code <inline>", "Inline code instead of a file path")
    .option("--timeout <seconds>", "Execution timeout in seconds (default 30)")
    .action(executeCommand);

  program
    .command("docs [slug]")
    .description("Fetch the skill-convention README for an app (or org index if slug omitted)")
    .option("--caller <hint>", "Caller density hint: internal | dashboard | external", "external")
    .action(docsCommand);

  program
    .command("mcp")
    .description("Run a stdio MCP bridge that proxies to mcp.clawnify.com (for Claude Desktop / ChatGPT / bash-less clients)")
    .action(mcpBridgeCommand);

  // `clawnify skills` — install Clawnify-curated skills (build-a-clawnify-app, ...)
  // into whichever coding agents have rules dirs in cwd (.claude, .cursor,
  // .openclaw, .codex, Aider CONVENTIONS.md). Skill contents are bundled in
  // the CLI; upgrade by bumping the CLI and re-running.
  const skills = program.command("skills").description("Install Clawnify-curated skills into your coding agent(s)");
  skills.command("list").description("List available skills").action(skillsListCommand);
  skills
    .command("add [name]")
    .description("Install a skill into every detected agent (Claude, Cursor, OpenClaw, Codex, Aider)")
    .option("--all", "Install every available skill")
    .option("--agent <agent>", "Force a specific target (claude | cursor | openclaw | codex | aider)")
    .action((name, opts) => skillsAddCommand(name, opts));
  skills
    .command("remove [name]")
    .description("Remove a previously-installed skill from detected agents")
    .option("--all", "Remove every installed skill")
    .option("--agent <agent>", "Restrict to a specific agent")
    .action((name, opts) => skillsRemoveCommand(name, opts));

  // `clawnify ai-files` — one step to make a project agent-ready: writes
  // the always-on Clawnify rules block into each detected agent's rules
  // file, installs the bundled skills, and wires the MCP server into
  // .mcp.json. Mirrors `npx convex ai-files install`.
  const aiFiles = program
    .command("ai-files")
    .description("Install Clawnify rules + skills + MCP into your coding agent(s)");
  aiFiles
    .command("install")
    .description("Write rules, install skills, and wire the MCP server (defaults to Claude if no agent is detected)")
    .option("--agent <agent>", "Force a specific target (claude | cursor | openclaw | codex | aider)")
    .option("--no-mcp", "Skip writing .mcp.json")
    .action((opts) => aiFilesInstallCommand(opts));
  aiFiles
    .command("uninstall")
    .description("Remove the Clawnify rules block, skills, and MCP server entry")
    .option("--agent <agent>", "Restrict to a specific agent")
    .action((opts) => aiFilesUninstallCommand(opts));

  const sdk = program.command("sdk").description("Inspect and manage the per-org SDK surface");
  sdk
    .command("inspect <slug>")
    .description("Show the typed-procedure README for an app (external density)")
    .option("--caller <hint>", "Caller density hint: internal | dashboard | external", "external")
    .action(sdkInspectCommand);
  sdk
    .command("login")
    .description("Authenticate / rotate the OAuth grant (alias of `clawnify login`)")
    .action(sdkLoginCommand);
  sdk
    .command("install")
    .description("(Deferred) Install @clawnify/sdk locally — pending Phase 5b")
    .action(sdkInstallCommand);

  try {
    await program.parseAsync();
  } catch (err) {
    console.error((err as Error)?.message ?? err);
    process.exitCode = 1;
  }

  // Force a prompt exit once a one-shot command finishes. Node's global
  // `fetch` (undici) keeps HTTP connections alive in a pool, which holds the
  // event loop open for several seconds after the command is actually done —
  // that's the "deploy printed ✓ but the prompt doesn't come back" hang.
  // Long-running commands (`dev`, `mcp`) never resolve `parseAsync`, so they
  // never reach this and keep running. Flush stdout first (the empty write's
  // callback fires after all prior writes drain) so piped/non-TTY output isn't
  // truncated by the exit.
  await new Promise<void>((resolve) => process.stdout.write("", () => resolve()));
  process.exit(process.exitCode ?? 0);
}
